import React, {useState} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Tooltip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Divider,
	CircularProgress,
	Typography,
	Paper,
	Fade, IconButton,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {Check as CheckIcon, Delete as DeleteIcon, FileCopy as CopyIcon} from "@material-ui/icons";
import moment from "moment";

const PRIMARY_COLOR = "#9cc54b";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		borderRadius: 16,
		overflow: "hidden",
		background: "#fdfdfd",
		boxShadow: "0px 10px 40px rgba(0,0,0,0.15)",
	},
	header: {
		fontWeight: 700,
		fontSize: "1.4rem",
		letterSpacing: "0.3px",
		padding: theme.spacing(3, 3, 2),
		borderBottom: "1px solid #e0e0e0",
		background: "#fff",
	},
	content: {
		padding: theme.spacing(3),
		backgroundColor: "#fafafa",
	},
	tableWrapper: {
		borderRadius: 12,
		overflow: "hidden",
		background: "#fff",
		boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
	},
	tableHead: {
		backgroundColor: "#f5f6f5",
	},
	headCell: {
		fontWeight: 600,
		fontSize: "0.95rem",
		color: "#333",
		textTransform: "uppercase",
		borderBottom: "2px solid #e0e0e0",
	},
	tableCell: {
		fontSize: "0.95rem",
		borderBottom: "1px solid #eee",
		color: "#444",
	},
	clickableRow: {
		transition: "background-color 0.2s ease",
		"&:hover": {
			backgroundColor: "#f9fdf5",
		},
	},
	deleteButton: {
		backgroundColor: "#e74c3c",
		color: "#fff",
		minWidth: 36,
		padding: theme.spacing(1),
		borderRadius: 8,
		transition: "all 0.25s ease",
		"&:hover": {
			backgroundColor: "#c0392b",
			transform: "scale(1.05)",
		},
	},
	actions: {
		justifyContent: "space-between",
		padding: theme.spacing(2.5, 3),
		backgroundColor: "#fff",
		borderTop: "1px solid #e0e0e0",
	},
	generateButton: {
		backgroundColor: PRIMARY_COLOR,
		color: "#fff",
		padding: theme.spacing(1, 4),
		borderRadius: 8,
		fontWeight: 600,
		fontSize: "0.95rem",
		boxShadow: "0 3px 10px rgba(156,197,75,0.4)",
		transition: "all 0.3s ease",
		"&:hover": {
			backgroundColor: "#89ad44",
			transform: "translateY(-1px)",
			boxShadow: "0 4px 12px rgba(156,197,75,0.5)",
		},
	},
	closeButton: {
		borderColor: "#ccc",
		color: "#666",
		fontWeight: 500,
		borderRadius: 8,
		padding: theme.spacing(1, 3),
		"&:hover": {
			backgroundColor: "#f9f9f9",
		},
	},
	loadingContainer: {
		textAlign: "center",
		padding: theme.spacing(6),
	},
	emptyText: {
		textAlign: "center",
		color: "#777",
		margin: theme.spacing(4, 0),
		fontSize: "1rem",
	},
}));

const ListCouponOwnerDialog = ({
	                               open,
	                               onClose,
	                               couponList = [],
	                               couponFetching,
	                               onDelete,
	                               onGenerate,
                               }) => {
	const classes = useStyles();
	const [copiedId, setCopiedId] = useState(null);

	const handleCopy = async (coupon) => {
		try {
			await navigator.clipboard.writeText(coupon.code);
			setCopiedId(coupon.id);
			setTimeout(() => setCopiedId(null), 1500);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			aria-labelledby="manage-coupon-dialog"
			classes={{paper: classes.dialogPaper}}
			TransitionComponent={Fade}
			TransitionProps={{timeout: 400}}
			style={{zIndex: 1}}
		>
			<DialogTitle disableTypography className={classes.header}>
				<Typography variant="h6">Manage Coupon</Typography>
			</DialogTitle>

			<DialogContent dividers className={classes.content}>
				{couponFetching ? (
					<div className={classes.loadingContainer}>
						<CircularProgress size={30} style={{color: PRIMARY_COLOR}}/>
						<Typography
							variant="body2"
							style={{marginTop: 12, color: "#555"}}
						>
							Loading coupons...
						</Typography>
					</div>
				) : couponList.length > 0 ? (
					<Paper elevation={0} className={classes.tableWrapper}>
						<Table>
							<TableHead className={classes.tableHead}>
								<TableRow>
									<TableCell className={classes.headCell}>Name</TableCell>
									<TableCell className={classes.headCell}>Code</TableCell>
									<TableCell className={classes.headCell}>Valid Date</TableCell>
									<TableCell
										className={classes.headCell}
										align="center"
										style={{width: "100px"}}
									>
										Action
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{couponList.map((coupon, idx) => {
									const isCopied = copiedId === coupon.id;
									console.log(coupon)
									return (
										<TableRow key={idx} className={classes.clickableRow}>
											<TableCell className={classes.tableCell}>
												{coupon?.name || "undefined"}
											</TableCell>
											<TableCell className={classes.tableCell}>
												<div style={{display: 'flex', alignItems: 'center', gap: 8}}>
													<span>{coupon?.code || "undefined"}</span>
													<Tooltip title={isCopied ? "Copied!" : "Copy Code"} placement="top">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation(); // prevent row click
																handleCopy(coupon);
															}}
														>
															{isCopied ? (
																<CheckIcon fontSize="small" style={{color: PRIMARY_COLOR}}/>
															) : (
																<CopyIcon fontSize="small"/>
															)}
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
											<TableCell className={classes.tableCell}>
												{moment(coupon.valid_to).format("DD/MM/YYYY HH:mm")}
											</TableCell>
											<TableCell align="center" className={classes.tableCell}>
												<Tooltip title="Delete Coupon">
													<Button
														className={classes.deleteButton}
														variant="contained"
														onClick={() => onDelete(coupon)}
													>
														<DeleteIcon fontSize="small"/>
													</Button>
												</Tooltip>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</Paper>
				) : (
					<Typography className={classes.emptyText}>
						No coupons found
					</Typography>
				)}
				<Divider style={{marginTop: "16px"}}/>
			</DialogContent>

			<DialogActions className={classes.actions}>
				<Button
					className={classes.closeButton}
					variant="outlined"
					onClick={onClose}
				>
					Close
				</Button>
				<Button
					className={classes.generateButton}
					variant="contained"
					onClick={onGenerate}
				>
					Generate New Coupon
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ListCouponOwnerDialog;
